import {AnyAction} from "redux";
import _ from "lodash";

import {IUserSettings, USER_SETTINGS_VERSION} from "../../data/IUserSettings";
import I18n from 'i18next';
import {
    MenuDto,
    ResourceListOfModelyearDto,
    ResourceListOfTypefamilyDto,
    CnrDto,
    LanguageDto,
    ModelyearDto,
    UserDto,
    UserDetailsDto,
    WorkflowDto,
    VersionInfoDto,
    InitDto,
    RoleDto,
    MaintenanceDto,
    MaintenanceListDto,
} from "../../services/Client";
import {config} from "../../services/Config";
import HATEOASClient from "../../services/HATEOASClient";
import {
    initSettings as initBaseOptionsViewUserSettings,
    validateSettings as validateBaseOptionsViewUserSettings
} from "../../views/BaseOptionsView/BaseOptionsViewSettings";
import {
    IOptionsViewUserSettings,
    initSettings as initOptionsViewUserSettings,
    validateSettings as validateOptionsViewUserSettings,
} from "../../views/OptionsView/OptionsViewSettings";
import UserSettingsDaemon from "../../services/UserSettingsDaemon";
import {LOCALTOKENKEY} from "../../services/BaseClient";
import {ActionTypes} from "../actions/ActionTypes";
import {ILocationTranslation} from "../../components/GeneralInterfaces";

export interface ITypefamilyMap {
    [mj1: string]: ResourceListOfTypefamilyDto;
}

export interface IGeneralState {
    client: HATEOASClient;
    settingsDaemon: UserSettingsDaemon;
    userInfo?: UserDto;
    userDetails?: UserDetailsDto;
    userRoles?: RoleDto[];
    userSettings: IUserSettings;
    locationTranslations: ILocationTranslation[];

    /** Base links into the back end API */
    baseLinks?: MenuDto;

    /** Extra links for the PSL area */
    pslAreaExtraLinks: MenuDto;

    /** Languages known to the back end */
    languageList?: LanguageDto[];

    /** All model years known to the PSL. */
    modelYears?: ResourceListOfModelyearDto;

    /** C-numbers known to the back end */
    cnrList?: CnrDto[];

    /** C-numbers to which the user has access */
    userCnrs: CnrDto[];
    /** Model years to which the user has access */
    userModelyears: ResourceListOfModelyearDto;
    /** Map of type families(per year) to which the user has access */
    userTypefamiliesMap: ITypefamilyMap;
    /** Model years available for PSL selection based on the typefamilies to which the user has access */
    userPSLModelyears: ResourceListOfModelyearDto;

    /** C-number for basic market */
    basicMarketCnr?: CnrDto;

    /** we are doing some work */
    loadingMessages?: String[];

    /** contains map of modelyear, ex. 'J' to its typefamilies  */
    typefamilyMap?: ITypefamilyMap;

    workflows?: WorkflowDto[];

    openApprovalsCount?: number;

    versionInfo?: VersionInfoDto;

    /** let's call it some kind of work mutexes ;) */
    loadingTechnicalData: boolean;
    loadingSpecConfis: boolean;
    loadingDerivates: boolean;
    loadingWorkflows: boolean;

    /** flag used to filter PSL table */
    hideEmptyAssignmentsRowsAndColumns: boolean;

    /** Extra links for the ModelBasicType area */
    modelBasicTypeExtraLinks: MenuDto;
    isShowEditMarket: boolean;
    maintenanceStatus: MaintenanceListDto ;
    translationService?: any;
}

export interface ITranslatedComponent {
    i18n: I18n.i18n;
    t: reactI18Next.TranslationFunction;
}

const initUserSettings = (state?: IGeneralState): IUserSettings => {
    if (state && state.userInfo && state.userInfo.userSettings) {
        const userSettings: IUserSettings = JSON.parse(state.userInfo.userSettings);
        if (userSettings && userSettings.version !== undefined &&
            userSettings.version === USER_SETTINGS_VERSION) {
            // the IUserSettings was successfully loaded.
            // validate specific subsections of the user settings.
            userSettings.baseOptionsView = validateBaseOptionsViewUserSettings(userSettings.baseOptionsView);
            userSettings.optionsView = validateOptionsViewUserSettings(userSettings.optionsView);
            return userSettings;
        } else {
            const receivedVersion = (userSettings && userSettings.version) ? userSettings.version : "unknown";
            const msg =
                `Unsupported user settings version \
                ${receivedVersion} received \
                (expected version ${USER_SETTINGS_VERSION}). \
                Falling back to factory user settings`;
            console.warn(msg);
        }
    }

    return {
        version: USER_SETTINGS_VERSION,
        recentWork: [],
        language: "en",
        optionsView: initOptionsViewUserSettings(),
        baseOptionsView: initBaseOptionsViewUserSettings(),
        pslEmailSubscriptions: [],
        switchHeaderMode: false,
    };
};

export const INITIAL_STATE: IGeneralState = {
    client: new HATEOASClient(config.baseURL),
    settingsDaemon: new UserSettingsDaemon(),
    userSettings: initUserSettings(),
    locationTranslations: [],
    loadingTechnicalData: false,
    loadingSpecConfis: false,
    loadingDerivates: false,
    loadingWorkflows: false,
    pslAreaExtraLinks: {},
    userCnrs: [],
    userModelyears: {},
    userTypefamiliesMap: {},
    userPSLModelyears: {},
    hideEmptyAssignmentsRowsAndColumns: false,
    modelBasicTypeExtraLinks: {},
    isShowEditMarket: false,
    maintenanceStatus: {},
};

declare global {
    interface Array<T> {
        indexOfComparer(needle: T, comparer: (left: T, right: T) => boolean): number;
    }

    interface SVGAnimatedString {
        indexOf(searchElement: string, fromIndex?: number): number;
    }
}

Array.prototype.indexOfComparer = function <T>(needle: T, comparer: (left: T, right: T) => boolean): number {
    for (let i = 0; i < this.length; i++) {
        let cur = this[i];
        if (comparer(cur, needle))
            return i;
    }
    return -1;
};

SVGAnimatedString.prototype.indexOf = function (searchElement: string, fromIndex?: number): number {
    return _.indexOf(this.baseVal, searchElement, fromIndex);
};

const assignUserInfo = (newState: IGeneralState, userInfo: UserDto): void => {
    newState.userInfo = userInfo;
    newState.userSettings = initUserSettings(newState);
};

const assignUserDetails = (newState: IGeneralState, userDetails: UserDetailsDto): void => {
    newState.userDetails = userDetails;
};

const assignVersionInfo = (newState: IGeneralState, versionInfo: VersionInfoDto): void => {
    newState.versionInfo = versionInfo;
};

const assignLanguages = (newState: IGeneralState, languageList: LanguageDto[]): void => {
    // Sort languages by code; the orderof the system languages affects
    // placement of localized texts such as the multi line column texts.
    if (newState && newState.userInfo && newState.userInfo.userSettings) {
        const userSettings: IUserSettings = JSON.parse(newState.userInfo.userSettings);
        if (userSettings.language === 'de') {
            newState.languageList = languageList.sort((langA, langB) => {
                const codeA = langA.nameDe.toLowerCase();
                const codeB = langB.nameDe.toLowerCase();
                if (codeA < codeB) {
                    return -1;
                } else if (codeA > codeB) {
                    return 1;
                } else {
                    return 0;
                }
            });
        } else if (userSettings.language === 'en') {
            newState.languageList = languageList.sort((langA, langB) => {
                const codeA = langA.nameEn.toLowerCase();
                const codeB = langB.nameEn.toLowerCase();
                if (codeA < codeB) {
                    return -1;
                } else if (codeA > codeB) {
                    return 1;
                } else {
                    return 0;
                }
            });
        }
    }
};

const assignModelyears = (newState: IGeneralState, modelyears: ResourceListOfModelyearDto): void => {
    newState.modelYears = modelyears;
};

const assignUserModelyears = (newState: IGeneralState, userModelyears: ResourceListOfModelyearDto): void => {
    newState.userModelyears = userModelyears;
};

const assignUserRoles = (newState: IGeneralState, userRoles: RoleDto[]): void => {
    newState.userRoles = userRoles;
};

const assignTypefamilies = (newState: IGeneralState, typefamilies: {[key: number]: ResourceListOfTypefamilyDto}): void => {
    if (newState.modelYears && newState.modelYears.elements) {
        const typefamilyMap = !newState.typefamilyMap ? {} : {...newState.typefamilyMap};
        newState.modelYears.elements.forEach((modelyear: ModelyearDto) => {
            if (typefamilies[modelyear.objectId] !== undefined) {
                const tfResList = typefamilies[modelyear.objectId];
                if (tfResList) {
                    typefamilyMap[modelyear.mj1] = tfResList;
                }
            }
        });
        newState.typefamilyMap = typefamilyMap;
    }
};

const assignUserTypefamilies = (newState: IGeneralState, typefamilies: {[key: number]: ResourceListOfTypefamilyDto}): void => {
    if (newState.userModelyears && newState.userModelyears.elements) {
        const userTypefamiliesMap = {};
        newState.userModelyears.elements.forEach((modelyear: ModelyearDto) => {
            if (typefamilies[modelyear.objectId] !== undefined) {
                const tfResList = typefamilies[modelyear.objectId];
                if (tfResList) {
                    userTypefamiliesMap[modelyear.mj1] = tfResList;
                }
            }
        });
        newState.userTypefamiliesMap = userTypefamiliesMap;
    }
};

const assignUserPSLModelyears = (newState: IGeneralState, typefamilies: {[key: number]: ResourceListOfTypefamilyDto}): void => {
    if (newState.userModelyears && newState.userModelyears.elements) {
        const userPSLModelyears: ModelyearDto[] = [];
        newState.userModelyears.elements.forEach((modelyear: ModelyearDto) => {
            if (typefamilies[modelyear.objectId] !== undefined) {
                const tfResList = typefamilies[modelyear.objectId];
                if (tfResList) {
                    userPSLModelyears.push(modelyear);
                }
            }
        });
        newState.userPSLModelyears = {
            elements: userPSLModelyears,
            links: newState.userModelyears.links
        };
    }
};

const assignCnrs = (newState: IGeneralState, cnrList: CnrDto[]): void => {
    newState.cnrList = cnrList;
};

const assignUserCnrs = (newState: IGeneralState, cnrList: CnrDto[]): void => {
    newState.userCnrs = cnrList;
};

const assignBasicMarketCnr = (newState: IGeneralState, basicMarketCnr: CnrDto): void => {
    newState.basicMarketCnr = basicMarketCnr;
};

const assignWorkflows = (newState: IGeneralState, workflowList: WorkflowDto[]): void => {
    newState.workflows = workflowList;
};

const assignOpenApprovalsCount = (newState: IGeneralState, openApprovalsCount: number): void => {
    newState.openApprovalsCount = openApprovalsCount;
};

const assignSettingsForOptionsView = (newState: IGeneralState, settings: IOptionsViewUserSettings) => {
    newState.userSettings = Object.assign({}, newState.userSettings, {optionsView: settings});
};

const assignInitData = (newState: IGeneralState, initDataDto: InitDto): void => {
    const {
        userInfo,
        userDetails,
        versionInfo,
        languages,
        modelyears,
        typefamilies,
        workflows,
        cnrs,
        basicMarketCnr,
        userRoles,
        openApprovalsCount,
        userCnrs,
        userModelyears,
        userTypefamilies
    } = initDataDto;

    if (userInfo)
        assignUserInfo(newState, userInfo); // initializes user settings internally

    if (userDetails)
        assignUserDetails(newState, userDetails);

    if (userRoles)
        assignUserRoles(newState, userRoles);

    if (versionInfo)
        assignVersionInfo(newState, versionInfo);

    if (languages && languages.elements)
        assignLanguages(newState, languages.elements);

    if (modelyears)
        assignModelyears(newState, modelyears);

    if (userModelyears && userModelyears.elements)
        assignUserModelyears(newState, userModelyears);

    if (typefamilies) {
        Object.keys(typefamilies).forEach((key) => {
            const tfResList = typefamilies[Number(key)];
            if (tfResList && tfResList.elements) {
                tfResList.elements = tfResList.elements.map((element: any) => ({
                    ...element,
                    key: element.typefamily === 'M' ? 'M_TYPEFAMILY' : element.typefamily, // Add key property
                }));
            }
        });
        assignTypefamilies(newState, typefamilies); // *after* model years were assigned
    }
    if (userTypefamilies) {
        Object.keys(userTypefamilies).forEach((key) => {
            const tfResList = userTypefamilies[Number(key)];
            if (tfResList && tfResList.elements) {
                tfResList.elements = tfResList.elements.map((element: any) => ({
                    ...element,
                    key: element.typefamily === 'M' ? 'M_TYPEFAMILY' : element.typefamily, // Add key property
                }));
            }
        });
        assignUserTypefamilies(newState, userTypefamilies);
        assignUserPSLModelyears(newState, userTypefamilies);
    }

    if (cnrs && cnrs.elements)
        assignCnrs(newState, cnrs.elements);

    if (userCnrs && userCnrs.elements)
        assignUserCnrs(newState, userCnrs.elements);

    if (basicMarketCnr)
        assignBasicMarketCnr(newState, basicMarketCnr);

    if (workflows && workflows.elements)
        assignWorkflows(newState, workflows.elements);

    if (openApprovalsCount !== undefined) {
        assignOpenApprovalsCount(newState, openApprovalsCount);
    }
};

export const generalReducer = (state: IGeneralState = INITIAL_STATE, action: AnyAction): IGeneralState => {
    let shouldUpdateUserSettings: boolean = false;
    let newState: IGeneralState | undefined;
    switch (action.type) {
        case ActionTypes.LOGIN:
            newState = Object.assign({}, state);
            sessionStorage.setItem(LOCALTOKENKEY, action.token);
            newState.client.token = action.token;
            break;
        case ActionTypes.RECEIVE_BASE_LINKS:
            newState = {
                ...state,
                baseLinks: action.baseLinks
            };
            break;
        case ActionTypes.RECEIVE_PSL_AREA_EXTRA_LINKS:
            newState = {
                ...state,
                pslAreaExtraLinks: action.pslAreaExtraLinks,
            };
            break;
        case ActionTypes.UPDATE_OPEN_APPROVALS_COUNT:
            newState = {
                ...state,
                openApprovalsCount: action.payload,
            };
            break;
        case ActionTypes.RECEIVE_INIT_DATA:
            newState = Object.assign({}, state);
            assignInitData(newState, action.initData);
            newState.settingsDaemon.install(newState);
            break;
        case ActionTypes.USER_INFO_LOADED:
            newState = Object.assign({}, state);
            assignUserInfo(newState, action.userInfo);
            newState.settingsDaemon.install(newState);
            break;
        case ActionTypes.CHANGE_LANGUAGE:
            newState = Object.assign({}, state);
            newState.userSettings.language = action.language;
            shouldUpdateUserSettings = true;
            break;
        case ActionTypes.CHANGE_TABLE_MODE:
            newState = Object.assign({}, state);
            newState.userSettings.switchHeaderMode = !newState.userSettings.switchHeaderMode;
            shouldUpdateUserSettings = true;
            break;
        case ActionTypes.ADD_LOCATION_TRANSLATION:
            newState = Object.assign({}, state);
            let idx = state.locationTranslations.indexOfComparer(
                action.translation,
                (left: ILocationTranslation, right: ILocationTranslation) =>
                    left.pattern instanceof RegExp && right.pattern instanceof RegExp ?
                        left.pattern.source === right.pattern.source :
                        left.pattern === right.pattern);
            if (idx !== -1)
                newState[idx] = action.translation;
            else
                newState.locationTranslations.push(action.translation);
            break;
        case ActionTypes.CHANGE_OPTIONSVIEW_USER_SETTINGS:
            newState = Object.assign({}, state);
            assignSettingsForOptionsView(newState, action.optionsView);
            shouldUpdateUserSettings = true;
            break;
        case ActionTypes.ADD_LOADING_MESSAGE:
            newState = Object.assign({}, state);
            if (!state.loadingMessages)
                newState.loadingMessages = [];
            if (newState.loadingMessages)
                newState.loadingMessages.push(action.message);
            break;
        case ActionTypes.REMOVE_LOADING_MESSAGE:
            if (state.loadingMessages) {
                newState = Object.assign({}, state);
                if (newState.loadingMessages) {
                    let res = _.remove(newState.loadingMessages, (el) => el !== action.message);
                    if (res.length === 0)
                        newState.loadingMessages = undefined;
                    else
                        newState.loadingMessages = res;
                }
            }
            break;
        case ActionTypes.SET_LOADING_MUTEX:
            newState = {
                ...state,
                [action.mutexName]: action.value
            };
            break;
        case ActionTypes.SET_FACTORY_USER_SETTINGS:
            newState = {
                ...state,
                userSettings: initUserSettings(),
            };
            newState.userSettings.switchHeaderMode = false;
            localStorage.removeItem("prompt_dynTable");
            localStorage.removeItem("prompt_dynTable_OptionsView");
            localStorage.removeItem("prompt_dynTable_BaseOption");
            localStorage.removeItem("prompt_dynTable_ModelTypeBasic");
            shouldUpdateUserSettings = true;
            break;
        case ActionTypes.ADD_RECENT_DOCUMENT:
            newState = Object.assign({}, state);
            const newApplink = action.appLink;
            // copy all recent work items but leave out the
            // current new app link if it already exists.
            const newRecentWork = _.filter(newState.userSettings.recentWork, appLink => appLink.href !== newApplink.href);
            newRecentWork.unshift(newApplink);
            if (newRecentWork.length > config.recentWorkCountMax) {
                // drop the oldest document
                newRecentWork.pop();
            }
            newState.userSettings.recentWork = [...newRecentWork];
            shouldUpdateUserSettings = true;
            break;
        case ActionTypes.RECEIVE_WORKFLOW_LIST:
            newState = Object.assign({}, state);
            assignWorkflows(newState, action.workflowList);
            break;
        case ActionTypes.SET_HIDE_EMPTY_ASSIGN_ROWS_AND_COLUMNS:
            newState = {
                ...state,
                hideEmptyAssignmentsRowsAndColumns: action.data !== undefined ? action.data : !state.hideEmptyAssignmentsRowsAndColumns,
            };
            break;
        case ActionTypes.RECEIVE_MODEL_TYPE_BASIC_EXTRA_LINKS:
            newState = {
                ...state,
                modelBasicTypeExtraLinks: action.modelTypeBasicExtraLinks
            };
            break;
        case ActionTypes.ISKUMA_MANAGER_ROLE_ONLY:
            newState = {
                ...state,
                isShowEditMarket: action.isShow
            };
            break;
        case ActionTypes.MAINTENANCE_STATUS:
            newState = {
                ...state,
                maintenanceStatus: action.maintenanceStatus
            };
            break;
        case ActionTypes.RECEIVE_TRANSLATION_SERVICE:
            newState = {
                ...state,
                translationService: action.translationService
            };
            break;
        default:
            break;
    }

    if (newState) {
        if (shouldUpdateUserSettings) {
            newState.settingsDaemon.updateState(newState);
            newState.settingsDaemon.save();
        }
        return newState;
    } else {
        return state;
    }
};



// WEBPACK FOOTER //
// ./src/redux/reducers/generalReducer.tsx